import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL, CDN_BASE_URL } from "../../config";

const FileUploadComponent = ({ orderId }) => {
  const [files, setFiles] = useState([]);
  const [previousFiles, setPreviousFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch previous files when component mounts
  useEffect(() => {
    const fetchPreviousFiles = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const response = await axios.get(`${BASE_URL}/order/${orderId}`, {
          headers: { "x-access-token": user.token },
        });

        if (response.data?.data.files && response.data?.data.files.length > 0) {
          setPreviousFiles(response.data?.data.files);
        }
      } catch (err) {
        console.error("Error fetching previous files:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviousFiles();
  }, [orderId, success]); // Refetch when upload is successful

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).map((file) => ({
      file,
      name: "",
      isNew: true, // Mark as new file
    }));
    setFiles([...files, ...newFiles]);
  };

  const handleNameChange = (index, value) => {
    const updatedFiles = [...files];
    updatedFiles[index].name = value;
    setFiles(updatedFiles);
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      const names = [];

      // Add only new files to FormData
      files
        .filter((file) => file.isNew)
        .forEach((item) => {
          formData.append("files", item.file);
          names.push(item.name);
        });

      const user = JSON.parse(localStorage.getItem("user"));

      // Add names array to FormData
      formData.append("names", JSON.stringify(names));

      await fetch(`${BASE_URL}/order/files/${orderId}`, {
        method: "PUT",
        body: formData,
        headers: { "x-access-token": user.token },
      });

      setSuccess(true);
      setFiles(files.filter((file) => !file.isNew)); // Keep only non-new files
    } catch (err) {
      setError(err.response?.data?.message || "خطایی رخ داده است");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFileInNewTab = (fileUrl) => {
    window.open(`${CDN_BASE_URL}${fileUrl}`, "_blank");
  };

  if (isLoading) {
    return <div>در حال بارگذاری...</div>;
  }

  return (
    <div className="file-upload-container min-w-[340px] lg:min-w-[400px]">
      <h2 className="text-lg font-bold mb-4">مدیریت فایل ها</h2>

      {/* Previous files section */}
      {previousFiles.length > 0 && (
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">فایل های قبلی:</h3>
          <div className="space-y-3">
            {previousFiles.map((file, index) => (
              <div
                key={`prev-${index}`}
                className="flex justify-between items-center p-3 border rounded"
              >
                <div>
                  <span className="text-sm">
                    {file.title || `فایل ${index + 1}`}
                  </span>
                </div>
                <button
                  onClick={() => openFileInNewTab(file.file)}
                  className="bg-blue-500 text-white text-sm rounded px-3 py-1"
                >
                  مشاهده فایل
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New files upload section */}
      <form onSubmit={handleSubmit} className="border-t pt-4">
        <div className="file-input-section mb-4">
          <label className="block mb-2">
            افزودن فایل جدید:
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              disabled={isSubmitting}
              className="block mt-1"
            />
          </label>
        </div>

        {files.length > 0 && (
          <div className="files-list mb-4">
            <h3 className="text-md font-semibold mb-2">فایل های جدید:</h3>
            <div className="space-y-3">
              {files.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-wrap justify-between items-center p-3 border rounded gap-2"
                >
                  <div className="flex-1 min-w-[200px]">
                    <span className="text-sm block mb-1">{item.file.name}</span>
                    <input
                      type="text"
                      placeholder="نام نمایشی فایل"
                      className="form-brand w-full"
                      value={item.name}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="bg-red-500 text-white text-sm rounded px-3 py-1"
                      disabled={isSubmitting}
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {files.length > 0 && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-green-500 text-white rounded px-4 py-2 disabled:bg-gray-400"
          >
            {isSubmitting ? "در حال آپلود..." : "ذخیره تغییرات"}
          </button>
        )}

        {error && (
          <div className="mt-3 text-red-500 bg-red-50 p-2 rounded text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-3 text-green-500 bg-green-50 p-2 rounded text-sm">
            تغییرات با موفقیت ذخیره شد!
          </div>
        )}
      </form>
    </div>
  );
};

export default FileUploadComponent;
